/**
 * <s-product-lightbox> — Amazon-style product image viewer.
 *
 * Markup contract:
 *   dialog                       — opened by gallery buttons via commandfor/command="show-modal".
 *   [data-lightbox-index]        — on the opening gallery button (inside the same <s-product-image-zoom>): which image to show first.
 *   img[data-lightbox-image]     — large image on the left.
 *   button[data-lightbox-thumb]  — thumbnails on the right, carrying data-index, data-src,
 *                                  data-srcset, data-alt, data-width, data-height.
 *
 * Behavior:
 *   Opening shows the clicked image. Clicking or hovering a thumbnail swaps the large image.
 *   Left/Right arrow keys step through images while the dialog is open.
 *   Backdrop close and focus return are handled by the wrapping <s-dialog>.
 */
class SProductLightbox extends HTMLElement {
  #ac;
  #index = 0;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    const image = this.querySelector('img[data-lightbox-image]');
    if (!dialog || !(image instanceof HTMLImageElement)) return;

    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const thumbs = [...this.querySelectorAll('[data-lightbox-thumb]')];

    const show = (index) => {
      if (!thumbs.length) return;
      const count = thumbs.length;
      const next = ((index % count) + count) % count;
      const thumb = thumbs[next];
      this.#index = next;
      image.removeAttribute('srcset');
      image.src = thumb.dataset.src;
      if (thumb.dataset.srcset) image.srcset = thumb.dataset.srcset;
      image.alt = thumb.dataset.alt || '';
      if (thumb.dataset.width) image.width = Number(thumb.dataset.width);
      if (thumb.dataset.height) image.height = Number(thumb.dataset.height);
      thumbs.forEach((item, i) => {
        if (i === next) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
    };

    // Gallery buttons live in the same gallery wrapper; their click runs before the dialog opens
    // (natively or through the command polyfill), so the right image is in place on open.
    const gallery = this.closest('s-product-image-zoom') || this.parentElement;
    gallery?.addEventListener(
      'click',
      (event) => {
        const opener = event.target.closest?.('[data-lightbox-index]');
        if (!opener || opener.getAttribute('commandfor') !== dialog.id) return;
        image.loading = 'eager';
        show(Number(opener.dataset.lightboxIndex ?? 0));
      },
      { signal }
    );

    thumbs.forEach((thumb) => {
      const select = () => show(Number(thumb.dataset.index ?? 0));
      thumb.addEventListener('click', select, { signal });
      thumb.addEventListener('mouseenter', select, { signal });
    });

    dialog.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          show(this.#index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          show(this.#index - 1);
        }
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-product-lightbox')) {
  customElements.define('s-product-lightbox', SProductLightbox);
}
